import { Row } from "@gadgetinc/widgets";
import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { DocsDropDownSelectors } from "./DocsDropDownSelectors";
import { HideWhenSidebarVisible } from "./nav/DocsPageTableOfContents";

const topNavBarHeight = 47;
const marginAboveSelectors = 12;
const selectorHeight = `${topNavBarHeight + marginAboveSelectors}px`;
const shownAnimationStyle = { top: selectorHeight };
const hiddenAnimationStyle = { top: `-${selectorHeight}` };
const scrollDistanceThreshold = 50; // Required scroll distance to show/hide the selectors

export const DocsPageHeaderSelectors = (props: { showInitially: boolean }) => {
  const { showInitially } = props;
  const { isShowing } = useScrollBasedVisibility({
    showInitially,
    scrollDistanceThreshold,
  });

  return (
    <>
      <AnimatePresence initial={false}>
        <motion.div
          style={{ position: "sticky", zIndex: 1 }}
          initial={isShowing ? hiddenAnimationStyle : shownAnimationStyle}
          animate={isShowing ? shownAnimationStyle : hiddenAnimationStyle}
          transition={{ duration: 0.2 }}
        >
          <HideWhenSidebarVisible>
            <Row style={{ paddingLeft: 12, gap: 10 }}>
              <DocsDropDownSelectors useFullFrameworkLabel addBoxShadow />
            </Row>
          </HideWhenSidebarVisible>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

const useScrollBasedVisibility = (props: { showInitially: boolean; scrollDistanceThreshold: number }) => {
  const { showInitially, scrollDistanceThreshold } = props;

  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [isShowing, setIsShowing] = useState(showInitially);
  const [scrollDirectionDistance, setScrollDirectionDistance] = useState(1);

  const handleScroll = useCallback(
    (e: Event) => {
      const currentScrollPosition = (e.target as Document)?.documentElement?.scrollTop ?? 0;
      const scrollDistance = currentScrollPosition - prevScrollPos;
      const didScrollUp = scrollDistance < 0;
      if (didScrollUp) {
        setScrollDirectionDistance(scrollDirectionDistance > 0 ? -1 : scrollDirectionDistance + scrollDistance);
        if (scrollDirectionDistance < -scrollDistanceThreshold || currentScrollPosition < scrollDistanceThreshold / 2) {
          setIsShowing(true);
        }
      } else {
        setScrollDirectionDistance(scrollDirectionDistance < 0 ? 1 : scrollDirectionDistance + scrollDistance);
        if (scrollDirectionDistance > scrollDistanceThreshold) {
          setIsShowing(false);
        }
      }
      setPrevScrollPos(currentScrollPosition);
    },
    [prevScrollPos, isShowing, scrollDirectionDistance]
  );

  useEffect(() => {
    // @ts-expect-error just for tests
    if (window.Cypress) {
      return;
    }

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return { isShowing };
};
